import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, ListItemText, ListSubheader, Menu, MenuItem, Tooltip, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { calendarColors } from '@/styles/theme';
import type { DateOnlyDayGroup } from '@/features/calendar/calendarDateOnly';
import type { CalendarServiceItem } from '@/types';

/**
 * A mechanic's work on a day that has no clock time (#2760).
 *
 * Imported history often knows only the day. Drawing each job as a timed block
 * put them all at shift start on top of each other, which reads as a schedule
 * that never happened. One whole-day entry says what is known — this many jobs,
 * this many hours — and lists them on click. Dotted rather than solid so it
 * never reads as a bookable slot.
 */
export function DateOnlyDayItem({
  group,
  onItemClick,
}: {
  group: DateOnlyDayGroup;
  onItemClick: (item: CalendarServiceItem) => void;
}) {
  const { t, i18n } = useTranslation('calendar');
  const hours = (value: number) => value.toLocaleString(i18n.language, { maximumFractionDigits: 1 });
  const theme = useTheme();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const tint = theme.palette.text.secondary;
  const summary = t('dateOnly.summary', {
    count: group.items.length,
    hours: hours(group.totalHours),
  });

  return (
    <>
      <Tooltip title={t('dateOnly.tooltip')}>
        <Box
          role="button"
          tabIndex={0}
          aria-haspopup="menu"
          aria-label={`${summary}. ${t('dateOnly.tooltip')}`}
          onClick={(e) => {
            e.stopPropagation();
            setAnchor(e.currentTarget);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setAnchor(e.currentTarget);
            }
          }}
          sx={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            px: 1,
            borderRadius: 0.5,
            border: `1px dashed ${calendarColors.itemBorder}`,
            color: tint,
            backgroundColor: alpha(tint, 0.08),
            cursor: 'pointer',
            overflow: 'hidden',
          }}
        >
          <Typography variant="caption" fontWeight={600} noWrap>{summary}</Typography>
        </Box>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={anchor !== null}
        onClose={() => setAnchor(null)}
        onClick={(e) => e.stopPropagation()}
        slotProps={{
          list: { subheader: <ListSubheader disableSticky>{t('dateOnly.listTitle')}</ListSubheader> },
        }}
      >
        {group.items.map((item) => (
          <MenuItem
            key={item.id}
            onClick={() => {
              setAnchor(null);
              onItemClick(item);
            }}
          >
            <ListItemText
              primary={item.vehicleRegNumber || item.serviceName}
              secondary={[item.serviceName, `${hours(item.durationHours)} h`, item.customerName]
                .filter(Boolean)
                .join(' · ')}
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
